import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../../core/common/dataTable/index";
import CollapseHeader from "../../../../core/common/collapse-header";
import { all_routes } from "../../../router/all_routes";
import { useUnidadesMedida } from "../../../../hooks/useUnidadesMedida";
import { apiService } from "../../../../services/api";
import { IUnidadeMedida, CreateUnidadeMedidaData } from '../../../../types/unidadesMedida';
import { Offcanvas } from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { modalUtils } from '../../../../utils/modalUtils';
import { ProtectedComponent } from "../../../../components/ProtectedComponent";

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: IUnidadeMedida) => React.ReactNode;
  sorter?: (a: IUnidadeMedida, b: IUnidadeMedida) => number;
}

export const UnidadesMedida = () => {
  const { unidadesMedida, loading, error, refetchUnidadesMedida } = useUnidadesMedida();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState<IUnidadeMedida[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Estado para controlar a unidade em edição
  const [editingUnidade, setEditingUnidade] = useState<IUnidadeMedida | null>(null);
  const [newUnidade, setNewUnidade] = useState<CreateUnidadeMedidaData>({
    codigo: '',
    nome: '',
    abreviacao: ''
  });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredData(unidadesMedida);
  }, [unidadesMedida]);

  // Função para filtrar unidades quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredData(unidadesMedida);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');
      
      const filtered = unidadesMedida.filter(item => {
        const searchableFields = [
          item.codigo,
          item.nome,
          item.abreviacao,
          Number(item.ativo) === 1 ? 'ativo' : 'inativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term => 
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredData(filtered);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(debounceTimeout);
  }, [searchText, unidadesMedida]);

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleCancelEdit = () => {
    setEditingUnidade(null);
  };

  const handleCancelNew = () => {
    setNewUnidade({
      codigo: '',
      nome: '',
      abreviacao: ''
    });
  };

  const handleEdit = (unidade: IUnidadeMedida) => {
    setEditingUnidade(unidade);
  };

  const handlePrepareDelete = (unidade: IUnidadeMedida) => {
    setDeletingId(unidade.id);
  };

  const columns: TableColumn[] = [
    {
      title: "Código",
      dataIndex: "codigo",
      sorter: (a, b) => a.codigo.localeCompare(b.codigo),
    },
    {
      title: "Nome",
      dataIndex: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
    },
    {
      title: "Abreviação",
      dataIndex: "abreviacao",
      render: (text) => text || '-',
    },
    {
      title: "Status",
      dataIndex: "ativo",
      render: (ativo) => {
        return (
          <div>
            {String(ativo) === "1" || ativo === 1 || ativo === true ? (
              <span className="badge badge-pill badge-status bg-success">Ativo</span>
            ) : (
              <span className="badge badge-pill badge-status bg-danger">Inativo</span>
            )}
          </div>
        );
      },
      sorter: (a, b) => {
        const aValue = Number(a.ativo);
        const bValue = Number(b.ativo);
        return aValue - bValue;
      },
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="PRODUTOS" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit_unidade"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="PRODUTOS" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_unidade"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      if (!newUnidade.codigo.trim()) {
        alert('O código é obrigatório');
        return;
      }

      await apiService.createUnidadeMedida(newUnidade);
      await refetchUnidadesMedida();

      handleCancelNew();

      const offcanvasElement = document.getElementById('offcanvas_unidade');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.classList.remove('offcanvas-open');
        modalUtils.abrirModal('success_unidade_modal');
      }, 300);

    } catch (error: any) {
      console.error('Erro ao criar unidade:', error);
      alert(error.message || 'Erro ao criar a unidade de medida.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingUnidade?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateUnidadeMedida(editingUnidade.id, editingUnidade);
      await refetchUnidadesMedida();

      const offcanvasElement = document.getElementById('offcanvas_edit_unidade');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      setTimeout(() => {
        document.querySelectorAll('.offcanvas-backdrop').forEach(el => el.remove());
        document.body.style.overflow = '';
        document.body.classList.remove('offcanvas-open');
        modalUtils.abrirModal('success_edit_unidade_modal');
      }, 300);

      handleCancelEdit();
    } catch (error: any) {
      console.error('Erro ao atualizar unidade:', error);
      alert(error.message || 'Erro ao atualizar a unidade de medida.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteUnidadeMedida(deletingId);
      await refetchUnidadesMedida();

      modalUtils.fecharModal('delete_unidade');

      setTimeout(() => {
        modalUtils.abrirModal('success_delete_unidade_modal');
      }, 400);

    } catch (error: any) {
      console.error('Erro ao excluir unidade:', error);
      alert(error.message || 'Erro ao excluir a unidade de medida.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="card">
            <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Unidades de Medida<span className="count-title">{filteredData.length} unidades</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Unidade"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button 
                              className="btn btn-sm position-absolute" 
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                    </div>

                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="PRODUTOS" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_unidade"
                        >
                          <i className="ti ti-plus me-2" />
                          Adicionar Unidade
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredData.length === 0 ? (
                      <div className="text-center py-5">
                        <h5 className="text-muted">Nenhuma unidade encontrada</h5>
                        <button className="btn btn-outline-primary mt-2" onClick={handleClearSearch}>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table 
                        dataSource={filteredData.map(item => ({ ...item, key: item.id.toString() }))} 
                        columns={columns} 
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_unidade">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Nova Unidade</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreate}>
            <div className="mb-3">
              <label className="col-form-label">Código <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={newUnidade.codigo}
                onChange={(e) => setNewUnidade(prev => ({ ...prev, codigo: e.target.value }))}
                required
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Nome</label>
              <input
                type="text"
                className="form-control"
                value={newUnidade.nome}
                onChange={(e) => setNewUnidade(prev => ({ ...prev, nome: e.target.value }))}
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Abreviação</label>
              <input
                type="text"
                className="form-control"
                value={newUnidade.abreviacao}
                onChange={(e) => setNewUnidade(prev => ({ ...prev, abreviacao: e.target.value }))}
              />
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button type="button" className="btn btn-light me-2" data-bs-dismiss="offcanvas">Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Criando...' : 'Criar'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Offcanvas Editar */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit_unidade">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Unidade</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas" onClick={handleCancelEdit}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => { e.preventDefault(); handleUpdate(); }}>
            <div className="mb-3">
              <label className="col-form-label">Código <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={editingUnidade?.codigo || ''}
                onChange={(e) => setEditingUnidade(prev => prev ? { ...prev, codigo: e.target.value } : null)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Nome</label>
              <input
                type="text"
                className="form-control"
                value={editingUnidade?.nome || ''}
                onChange={(e) => setEditingUnidade(prev => prev ? { ...prev, nome: e.target.value } : null)}
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Abreviação</label>
              <input
                type="text"
                className="form-control"
                value={editingUnidade?.abreviacao || ''}
                onChange={(e) => setEditingUnidade(prev => prev ? { ...prev, abreviacao: e.target.value } : null)}
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label">Status</label>
              <select 
                className="form-select"
                value={Number(editingUnidade?.ativo)}
                onChange={(e) => setEditingUnidade(prev => prev ? { ...prev, ativo: Number(e.target.value) } : null)}
              >
                <option value={1}>Ativo</option>
                <option value={0}>Inativo</option>
              </select>
            </div>
            <div className="d-flex align-items-center justify-content-end">
              <button type="button" className="btn btn-light me-2" data-bs-dismiss="offcanvas" onClick={handleCancelEdit}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Deletar */}
      <div className="modal fade" id="delete_unidade" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                <i className="ti ti-trash-x fs-36 text-danger" />
              </div>
              <h4>Remover Unidade?</h4>
              <p>Tem certeza que deseja remover a unidade selecionada?</p>
              <div className="d-flex justify-content-center mt-4">
                <button className="btn btn-light me-2" data-bs-dismiss="modal">Cancelar</button>
                <button className="btn btn-danger" onClick={handleDelete} disabled={isDeleting}>
                  {isDeleting ? 'Excluindo...' : 'Sim, Deletar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modais de Sucesso */}
      <div className="modal fade" id="success_unidade_modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                <i className="ti ti-check-check fs-36 text-success" />
              </div>
              <h3>Unidade Criada!</h3>
              <button className="btn btn-primary mt-4" onClick={() => modalUtils.fecharModal('success_unidade_modal')}>OK</button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="success_edit_unidade_modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                <i className="ti ti-check-check fs-36 text-success" />
              </div>
              <h3>Unidade Atualizada!</h3>
              <button className="btn btn-primary mt-4" onClick={() => modalUtils.fecharModal('success_edit_unidade_modal')}>OK</button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal fade" id="success_delete_unidade_modal" tabIndex={-1}>
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                <i className="ti ti-check-check fs-36 text-success" />
              </div>
              <h3>Unidade Excluída!</h3>
              <button className="btn btn-primary mt-4" onClick={() => modalUtils.fecharModal('success_delete_unidade_modal')}>OK</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
